import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Pin, PinOff } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useToast } from "@/context/ToastContext";
import { cn } from "@/lib/utils";
import type { NavGroup, NavItem } from "./nav";
import type { PinnedNav } from "./usePinnedNav";

const badgeText = (n: number) => (n > 99 ? "99+" : String(n));

/** One module in the menu: a link, with a pin button to put it on (or take it off) the sidebar */
function ItemRow({ item, active, pinned, onPin, onNavigate }: { item: NavItem; active: boolean; pinned: boolean; onPin: () => void; onNavigate: () => void }) {
  const Icon = item.icon;
  return (
    <div className="group/row relative flex items-center">
      <Link
        to={item.to}
        data-row
        onClick={onNavigate}
        title={item.badge && item.badgeTitle ? item.badgeTitle : undefined}
        aria-current={active ? "page" : undefined}
        className={cn(
          "flex h-10 min-w-0 flex-1 items-center gap-3 rounded-lg px-3 pr-10 text-[13.5px] outline-none transition-colors focus-visible:bg-sidebar-accent focus-visible:ring-1 focus-visible:ring-sidebar-ring",
          active ? "bg-sidebar-primary font-semibold text-sidebar-primary-foreground" : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
        )}
      >
        <Icon className="size-[18px] shrink-0" />
        <span className="min-w-0 flex-1 truncate">{item.label}</span>
        {item.badge > 0 && <span className="flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-gold px-1.5 text-[10px] font-bold leading-none text-sidebar">{badgeText(item.badge)}</span>}
      </Link>
      <button
        type="button"
        onClick={onPin}
        aria-pressed={pinned}
        aria-label={pinned ? `Remove ${item.label} from the sidebar` : `Pin ${item.label} to the sidebar`}
        title={pinned ? "On the sidebar. Click to remove" : "Pin to the sidebar"}
        className={cn(
          "absolute right-1.5 flex size-7 items-center justify-center rounded-md text-sidebar-foreground outline-none transition-opacity hover:bg-white/10 hover:text-white focus-visible:opacity-100 focus-visible:ring-1 focus-visible:ring-sidebar-ring",
          pinned ? "opacity-70" : "opacity-0 group-hover/row:opacity-100",
        )}
      >
        {pinned ? <Pin className="size-3.5 fill-current" /> : <PinOff className="size-3.5" />}
      </button>
    </div>
  );
}

/**
 * The "More" menu: categories on the left, the pages of the highlighted category on the right (like HubSpot's).
 * On a phone it is one scrolling list, since there is no hover.
 */
export function MoreMenu({
  groups,
  isActive,
  pins,
  desktop,
  trigger,
  onOpenChange,
}: {
  groups: NavGroup[];
  isActive: (item: NavItem) => boolean;
  pins: PinnedNav;
  desktop: boolean;
  trigger: React.ReactNode;
  /** Lets the rail hide the button's tooltip while the menu is open */
  onOpenChange?: (open: boolean) => void;
}) {
  const { error } = useToast();
  const [open, setOpenState] = useState(false);
  const setOpen = (value: boolean) => {
    setOpenState(value);
    onOpenChange?.(value);
  };
  const rootRef = useRef<HTMLDivElement>(null);
  const currentGroup = groups.find((g) => g.items.some(isActive))?.key || groups[0]?.key || "";
  const [category, setCategory] = useState(currentGroup);

  // Opens on the category of the page you are on
  useEffect(() => {
    if (open) setCategory(currentGroup);
  }, [open, currentGroup]);

  const close = () => setOpen(false);
  const pin = (item: NavItem) => {
    if (!pins.toggle(item.key)) error("The sidebar is full", `Up to ${pins.max} modules can be pinned. Remove one first.`);
  };

  const focusIn = (selector: string, which: "first" | "last" | number = "first") => {
    const list = Array.from(rootRef.current?.querySelectorAll<HTMLElement>(selector) || []);
    const el = which === "first" ? list[0] : which === "last" ? list[list.length - 1] : list[which];
    el?.focus();
  };
  const step = (e: React.KeyboardEvent, selector: string, delta: number) => {
    const list = Array.from(rootRef.current?.querySelectorAll<HTMLElement>(selector) || []);
    const i = list.indexOf(document.activeElement as HTMLElement);
    if (i < 0) return;
    e.preventDefault();
    list[(i + delta + list.length) % list.length]?.focus();
  };

  const shown = groups.find((g) => g.key === category) || groups[0];
  // One fixed height, sized for the longest category, so hovering from one category to another never resizes
  // (and so never moves) the menu; the pages column scrolls if the window is shorter
  const longest = Math.max(1, ...groups.map((g) => g.items.length));
  const height = Math.max(260, Math.min(longest * 40 + 70, 560));

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        side="right"
        align="end"
        sideOffset={12}
        collisionPadding={12}
        onOpenAutoFocus={(e) => {
          // Focus the category list so the arrow keys work straight away
          e.preventDefault();
          requestAnimationFrame(() => focusIn("[data-cat]"));
        }}
        className={cn("overflow-hidden border-sidebar-border bg-sidebar p-0 text-sidebar-foreground shadow-2xl", desktop ? "w-[540px]" : "w-[calc(100vw-96px)] max-w-[420px]")}
      >
        <div ref={rootRef}>
          {desktop ? (
            <div className="flex" style={{ height: `min(${height}px, calc(100vh - 48px))` }}>
              <div role="menu" aria-label="More" className="w-[210px] shrink-0 overflow-y-auto border-r border-sidebar-border p-2.5" onKeyDown={(e) => {
                if (e.key === "ArrowDown") step(e, "[data-cat]", 1);
                else if (e.key === "ArrowUp") step(e, "[data-cat]", -1);
                else if (e.key === "ArrowRight" || e.key === "Enter") {
                  e.preventDefault();
                  focusIn("[data-row]");
                }
              }}>
                <div className="px-2.5 pb-2 pt-1.5 text-[15px] font-semibold text-white">More</div>
                {groups.map((g) => (
                  <button
                    key={g.key}
                    type="button"
                    role="menuitem"
                    data-cat
                    onMouseEnter={() => setCategory(g.key)}
                    onFocus={() => setCategory(g.key)}
                    onClick={() => {
                      setCategory(g.key);
                      focusIn("[data-row]");
                    }}
                    aria-haspopup="true"
                    aria-expanded={shown?.key === g.key}
                    className={cn(
                      "flex h-10 w-full items-center justify-between rounded-lg px-3 text-left text-[13.5px] outline-none transition-colors",
                      shown?.key === g.key ? "bg-sidebar-accent text-white" : "text-sidebar-foreground hover:bg-sidebar-accent/60",
                    )}
                  >
                    <span>{g.title}</span>
                    <ChevronRight className="size-4 opacity-70" />
                  </button>
                ))}
              </div>
              <div
                className="min-w-0 flex-1 overflow-y-auto p-2.5"
                onKeyDown={(e) => {
                  if (e.key === "ArrowDown") step(e, "[data-row]", 1);
                  else if (e.key === "ArrowUp") step(e, "[data-row]", -1);
                  else if (e.key === "ArrowLeft") {
                    e.preventDefault();
                    const i = groups.findIndex((g) => g.key === shown?.key);
                    focusIn("[data-cat]", Math.max(i, 0));
                  }
                }}
              >
                <div className="px-2.5 pb-2 pt-1.5 text-[11px] font-semibold uppercase tracking-[0.1em] text-sidebar-foreground/60">{shown?.title}</div>
                {shown?.items.map((item) => (
                  <ItemRow key={item.key} item={item} active={isActive(item)} pinned={pins.isPinned(item.key)} onPin={() => pin(item)} onNavigate={close} />
                ))}
              </div>
            </div>
          ) : (
            <div className="max-h-[70vh] overflow-y-auto p-2.5">
              {groups.map((g) => (
                <div key={g.key} className="pb-2">
                  <div className="px-2.5 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-[0.1em] text-sidebar-foreground/60">{g.title}</div>
                  {g.items.map((item) => (
                    <ItemRow key={item.key} item={item} active={isActive(item)} pinned={pins.isPinned(item.key)} onPin={() => pin(item)} onNavigate={close} />
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
